import { AxiosError, AxiosResponse } from 'axios';
import { useFormik } from 'formik';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';

import { cpfMask, createUrl, cnpjMask, request, useFormikHandles, formatDate, dateMask, phoneMask, cepMask, createMarkup } from 'app/utils';
import { Box, Button, Checkbox, Flex, HelperText, Hr, Input, Label, Loading, Page, Radio, Section, Select, Typography } from 'components';
import { useSchool } from 'features/school';
import styled from 'styled-components';
import { useMenu } from 'features/menu';
import { useAccount } from 'features/account';
import { ChangeEmail, ConfirmCurrentEmail, ConfirmNewEmail } from 'features/account/components';
import { AccountApiResponse, CustomersTypes, GenerateFormItem } from 'features/account/types';
import Alert, { useAlert } from 'features/alert';
import { useNavigate } from 'react-router-dom';
import Icons from 'components/icons';
import { useAuth } from 'features/auth';
import SubmenuVertical from 'components/submenuVertical';
import { HeaderPerfil } from 'features/account/components';

const FormIcon = styled(Icons)`
  color: ${({ theme }) => theme.colors.black30};

  ${({ theme: { mediaQueries } }) => {
    return `
      width: 25px;

      ${mediaQueries.desktop} {
        width: 28px;
      }
    `;
  }}
`;

const TextIcon = styled(Icons)`
  color: ${({ theme }) => theme.colors.gray_300};

  ${({ theme: { mediaQueries } }) => {
    return `
      width: 16px;

      ${mediaQueries.desktop} {
        width: 16px;
      }
    `;
  }}
`;

const IconWrapper = styled(Flex)`
  position: absolute;
  top: 50%;
  left: 5px;
  transform: translateY(-50%);
`;

const InputIcon = styled(Input)`
  padding-left: 40px;
`;

const AccountView = ({ confirmCurrentEmail, confirmNewEmail }: { confirmCurrentEmail?: boolean; confirmNewEmail?: boolean } = {}): ReactElement => {
  const [loading, setLoading] = useState(true);
  const [loadingEdit, setLoadingEdit] = useState(false);

  const [changeEmailOpen, setChangeEmailOpen] = useState(false);

  const [subAreaIndex, setSubAreaIndex] = useState(0);

  const { apiGatewayUrl } = useSchool();
  const { setBackPathname, clearBackPathname } = useMenu();
  const { setAlert, clearAlert } = useAlert();
  const { account, setAccount, clearAccount } = useAccount();
  const { sso } = useAuth();

  const fetch = async () => {
    await Promise.all([
      request({
        url: `/api/v1/School/MyAccount`,
        method: 'get',
        baseURL: apiGatewayUrl,
        data: {
          productSlug: null,
          specialOnly: null,
          itensPerQuery: 8,
        },
      }).then(({ data }: AxiosResponse<AccountApiResponse>) => {
        setAccount({
          ...data,
          birthOrFoundation: data.birthOrFoundation.replace('1970-01-01T00:00:00+00:00', ''),
          customerType: data.customerType || CustomersTypes.Person,
        });
      }),
    ]).finally(() => {
      setLoading(false);
    });
  };

  const customMaskPhone = (phone: string, isInternational: boolean) => {
    return isInternational ? phone : phoneMask(phone.replace('+55', '').trim());
  };

  const initialValues: Record<string, any> = {
    ...account,

    accountName: account?.accountName || account?.name || '',

    contactPhone: account && account.contactPhone ? customMaskPhone(account.contactPhone, account.internationalContactPhone) : '',

    contactWhatsApp: account && account.contactWhatsApp ? customMaskPhone(account.contactWhatsApp, account.internationalContactWhatsApp) : '',

    taxId: account && account.taxId ? account.taxId : '',

    birthOrFoundation: account && account.birthOrFoundation ? formatDate(account.birthOrFoundation).utc().format('DD/MM/YYYY') : '',
    addressZipCode: account && account.addressZipCode ? account.addressZipCode : '',
    addressLine1: account && account.addressLine1 ? account.addressLine1 : '',
    addressNumber: account && account.addressNumber ? account.addressNumber : '',
    addressNeighborhood: account && account.addressNeighborhood ? account.addressNeighborhood : '',
    addressCity: account && account.addressCity ? account.addressCity : '',
    addressState: account && account.addressState ? account.addressState : '',
  };

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      accountName: Yup.string().required('Você precisa informar seu nome completo'),

      birthOrFoundation: Yup.string().required(
        `Você precisa informar ${account && account.customerType === 1 ? 'sua data de nascimento' : 'a data de fundação da empresa'}`
      ),

      taxId: Yup.string().required(`Você precisa informar ${account && account.customerType === 1 ? 'seu CPF' : 'o CNPJ da empresa'}`),

      addressZipCode: Yup.string().required(`Informe seu CEP`),
      addressLine1: Yup.string().required(`Informe seu endereço`),
      addressNumber: Yup.string().required(`Informe o número`),
      addressNeighborhood: Yup.string().required(`Informe seu bairro`),
      addressCity: Yup.string().required(`Informe sua cidade`),
      addressState: Yup.string().required(`Informe a UF`),
    }),
    enableReinitialize: true,
    onSubmit: (values, actions) => {
      setLoadingEdit(true);

      let birthOrFoundation = '';

      if (values.birthOrFoundation) {
        const [birthDay, birthMonth, birthYear] = values.birthOrFoundation.split('/');
        birthOrFoundation = formatDate(`${birthYear}-${birthMonth}-${birthDay} 00:00:00`).format('YYYY-MM-DD[T]HH:mm:ss.SSSZ');
      }

      request({
        url: `/api/v1/School/MyAccount`,
        method: 'put',
        baseURL: apiGatewayUrl,
        data: {
          ...values,
          maritalStatus: Number(values.maritalStatus),
          internationalContactPhone: Boolean(values.internationalContactPhone),
          internationalContactWhatsApp: Boolean(values.internationalContactWhatsApp),
          birthOrFoundation,
        },
      })
        .then(() => {
          setAlert({ type: 'success', message: 'Seus dados foram atualizados com sucesso.' });
        })
        .catch((e: AxiosError<{ apiResultMessage: string }>) => {
          setAlert({
            type: 'error',
            message: e?.response?.data?.apiResultMessage || 'Ocorreu um erro ao salvar seus dados. Por favor, tente novamente.',
          });
        })
        .finally(() => {
          setLoadingEdit(false);
        });
    },
  });

  const { formikHandleFocus, formikHandleBlur, formikHandleChange } = useFormikHandles(formik);

  useEffect(() => {
    if (sso) navigate(createUrl('dashboard'));

    fetch();

    setBackPathname(createUrl('dashboard'));

    return () => {
      clearAccount();
      clearBackPathname();
      clearAlert();
    };
  }, []);

  const generateFormFirst: () => GenerateFormItem[][] = useCallback(
    () => [
      [
        {
          name: 'customerType',
          formType: 'radio',
          radioOptions: [
            { value: CustomersTypes.Person, label: 'Pessoa Física' },
            { value: CustomersTypes.Company, label: 'Pessoa Jurídica' },
          ],
        },
      ],

      [
        {
          name: 'accountName',
          label: formik.values.customerType === 1 ? 'Nome completo' : 'Razão Social',
          formType: 'input',
          inputType: 'text',
          maxLength: 50,
          width: { m: '100%', d: 400 },
          rightText: 'Preencha corretamente pois será usado<br />na confeção dos certificados.',
          // value: formik.values.accountName || formik.values.name || '',
        },
      ],
      [
        {
          name: 'alias',
          label: formik.values.customerType === 1 ? 'Apelido' : 'Nome fantasia',
          formType: 'input',
          inputType: 'text',
          maxLength: 50,
          width: { m: '100%', d: 400 },
        },
      ],

      [
        {
          name: 'birthOrFoundation',
          label: formik.values.customerType === 1 ? 'Data de Nascimento' : 'Data de abertura',
          formType: 'input',
          inputType: 'text',
          maxLength: 10,
          mask: dateMask,
          width: { m: '100%', d: 250 },
        },
      ],
      [
        {
          name: 'accountLogin',
          label: 'E-mail',
          formType: 'input',
          inputType: 'text',
          maxLength: 50,
          width: { m: '100%', d: 460 },
          rightText: 'Esse será seu login de<br />acesso a plataforma.',
          disabled: true,
        },
      ],
      [
        {
          name: 'taxId',
          label: formik.values.customerType === 1 ? 'CPF' : 'CNPJ',
          formType: 'input',
          inputType: 'text',
          maxLength: 50,
          mask: formik.values.customerType === 1 ? cpfMask : cnpjMask,
          width: { m: '100%', d: 300 },
          disabled: !!account?.taxId,
        },
      ],

      [
        {
          name: 'maritalStatus',
          label: 'Estado civil',
          formType: 'select',
          selectFirstLabel: 'Selecione...',
          selectFirstValue: 0,
          selectOptions: [
            { value: 1, label: 'Solteiro(a)' },
            { value: 2, label: 'Casado(a)' },
            { value: 3, label: 'União civil' },
            { value: 4, label: 'União estável' },
            {
              value: 5,
              label: 'Divorciado(a)',
            },
            { value: 6, label: 'Viúvo(a)' },
          ],
          width: { m: '100%', d: 300 },
          hide: formik.values.customerType === 2,
        },
      ],
    ],
    [formik]
  );

  const generateFormContact: () => GenerateFormItem[][] = useCallback(
    () => [
      [
        {
          name: 'contactPhone',
          label: 'Telefone',
          formType: 'input',
          inputType: 'text',
          maxLength: formik.values.internationalContactPhone ? undefined : 15,
          mask: (phone) => customMaskPhone(phone, formik.values.internationalContactPhone),
          width: { m: '100%', d: 350 },
          after: [
            {
              name: 'internationalContactPhone',
              formType: 'checkbox',
              checkboxLabel: 'Internacional',
            },
          ],
        },
      ],
      [
        {
          name: 'contactWhatsApp',
          label: 'Whatsapp',
          formType: 'input',
          inputType: 'text',
          maxLength: formik.values.internationalContactWhatsApp ? undefined : 15,
          mask: (phone) => customMaskPhone(phone, formik.values.internationalContactWhatsApp),
          width: { m: '100%', d: 350 },
          after: [
            {
              name: 'internationalContactWhatsApp',
              formType: 'checkbox',
              checkboxLabel: 'Internacional',
            },
          ],
        },
      ],

      [
        {
          name: 'contactWebSiteUrl',
          label: 'Website',
          formType: 'input',
          inputType: 'text',
          maxLength: 50,
          width: { m: '100%', d: 350 },
        },
      ],
      [
        {
          name: 'socialInstagram',
          label: 'Instagram',
          icon: 'instagram',
          formType: 'input',
          inputType: 'text',
          maxLength: 100,
          width: { m: '100%', d: 480 },
        },
      ],
      [
        {
          name: 'socialFacebook',
          label: 'Facebook',
          icon: 'facebook',
          formType: 'input',
          inputType: 'text',
          maxLength: 100,
          width: { m: '100%', d: 480 },
        },
      ],
      [
        {
          name: 'socialTwitter',
          label: 'Twitter',
          icon: 'twitter',
          formType: 'input',
          inputType: 'text',
          maxLength: 100,
          width: { m: '100%', d: 480 },
        },
      ],
      [
        {
          name: 'socialLinkedIn',
          label: 'LinkedIn',
          icon: 'linkedin',
          formType: 'input',
          inputType: 'text',
          maxLength: 100,
          width: { m: '100%', d: 480 },
        },
      ],
    ],
    [formik]
  );

  const generateFormAddress: () => GenerateFormItem[][] = useCallback(
    () => [
      [
        {
          name: 'addressZipCode',
          label: 'CEP',
          formType: 'input',
          inputType: 'text',
          maxLength: 9,
          mask: cepMask,
          width: { m: '100%', d: 200 },
          inputOnChange: (event: React.ChangeEvent<HTMLInputElement>) => {
            formikHandleChange(event);

            if (event.target.value.length === 9) {
              setLoadingEdit(true);

              request({
                url: `https://cep.awesomeapi.com.br/json/${event.target.value}`,
                method: 'get',
              })
                .then(
                  (
                    response: AxiosResponse<{
                      cep: string;
                      address_type: string;
                      address_name: string;
                      address: string;
                      state: string;
                      district: string;
                      lat: string;
                      lng: string;
                      city: string;
                      city_ibge: string;
                      ddd: string;
                    }>
                  ) => {
                    setLoadingEdit(false);

                    const { address, state, district, city } = response.data;

                    formik.setFieldValue('addressLine1', `${address}`);
                    formik.setFieldValue('addressNeighborhood', district);
                    formik.setFieldValue('addressCity', city);
                    formik.setFieldValue('addressState', state);
                  }
                )
                .catch(() => {
                  setLoadingEdit(false);
                });
            }
          },
        },
      ],
      [
        {
          name: 'addressLine1',
          label: 'Endereço',
          formType: 'input',
          inputType: 'text',
          maxLength: 50,
          width: { m: '100%', d: 350 },
        },
        {
          name: 'addressNumber',
          label: 'Número',
          formType: 'input',
          inputType: 'text',
          maxLength: 50,
          width: 130,
        },
      ],
      [
        {
          name: 'addressLine2',
          label: 'Complemento',
          formType: 'input',
          inputType: 'text',
          maxLength: 50,
          width: { m: '100%', d: 350 },
        },
      ],
      [
        {
          name: 'addressNeighborhood',
          label: 'Bairro',
          formType: 'input',
          inputType: 'text',
          maxLength: 50,
          width: { m: '100%', d: 485 },
        },
      ],
      [
        {
          name: 'addressCity',
          label: 'Cidade',
          formType: 'input',
          inputType: 'text',
          maxLength: 50,
          width: { m: '100%', d: 370 },
        },
        {
          name: 'addressState',
          label: 'UF',
          formType: 'input',
          inputType: 'text',
          maxLength: 50,
          width: 100,
        },
      ],

      [
        {
          name: 'addressCountry',
          label: 'País',
          formType: 'select',
          selectFirstLabel: 'Selecione...',
          selectFirstValue: 0,
          selectOptions: [{ value: 'BR', label: 'Brasil' }],
          width: { m: '100%', d: 270 },
          disabled: true,
        },
      ],
    ],
    [formik]
  );

  const navigate = useNavigate();

  if (loading) return <Loading label="Por favor, aguarde." />;

  return (
    <Page title="Meu Perfil">
      {loadingEdit && <Loading alpha />}
      <HeaderPerfil areaIndex={0} onAreaClick={(value) => navigate(createUrl(value))} />
      <Flex flexDirection="row" bg="white">
        <SubmenuVertical
          // areas={['Dados pessoais', 'Dados de contato', 'Dados profissionais', 'Dados financeiros']}
          areas={['Dados pessoais', 'Dados de contato', 'Endereço', 'Trocar endereço de e-mail']}
          areaIndex={subAreaIndex}
          onAreaClick={(index) => setSubAreaIndex(index)}
        />
        <form onSubmit={formik.handleSubmit}>
          <Flex py={{ m: 30, t: 60, d: 60 }} bg="white" flexGrow={0} marginLeft="2%">
            <Section flexDirection={{ m: 'column', d: 'row' }} justifyContent="space-between" flexWrap="nowrap">
              <Flex flexGrow={1} flexDirection="column">
                {subAreaIndex === 0
                  ? generateFormFirst().map((rowItem, rowIndex) => {
                      return (
                        <Flex
                          key={`form-first-${rowIndex.toString()}`}
                          flexGrow={{ m: 1, d: 0 }}
                          flexDirection={{ m: 'column', d: 'row' }}
                          flexWrap={{ m: 'wrap', d: 'nowrap' }}
                        >
                          {rowItem.map((item, index) =>
                            item.hide ? (
                              <></>
                            ) : (
                              <Flex
                                key={`form-first-item-${item.name}`}
                                flexGrow={{ m: 1, d: 0 }}
                                // flexDirection={{ m: 'column', d: 'row' }}
                                // flexWrap={{ m: 'wrap', d: 'nowrap' }}
                                // width={{ m: '100%', d: 'inherit' }}
                              >
                                <Flex
                                  flexDirection="column"
                                  mt={4}
                                  ml={{ d: index ? 3 : 0 }}
                                  width={item && item.width ? item.width : 'inherit'}
                                  maxWidth={item && item.maxWidth ? item.maxWidth : 'inherit'}
                                  flexShrink={0}
                                  flexGrow={1}
                                >
                                  <Flex justifyContent="space-between">
                                    <Flex flexGrow={0}>
                                      <Typography type="type1" fontWeight="bold" upper>
                                        <Label htmlFor={`item-${item.name}`}>{item.label}</Label>
                                      </Typography>
                                    </Flex>
                                  </Flex>

                                  <Flex flexDirection="column">
                                    <Flex mt={1} flexWrap="nowrap" alignItems="center">
                                      <Flex flexGrow={1} flexWrap="nowrap" alignItems="center">
                                        {item.before && item.before.length ? (
                                          item.before.map((itemBefore, indexBefore) => {
                                            return (
                                              <Box
                                                flexGrow={0}
                                                key={`form-itemBefore-${itemBefore.name}`}
                                                width={itemBefore.width ? itemBefore.width : 'inherit'}
                                                maxWidth={itemBefore.maxWidth ? itemBefore.maxWidth : 'inherit'}
                                                mr={2}
                                              >
                                                {itemBefore.formType === 'input' && (
                                                  <>
                                                    <Input
                                                      id={`item-${itemBefore.name}`}
                                                      name={itemBefore.name}
                                                      type={itemBefore.inputType}
                                                      onChange={formikHandleChange}
                                                      onFocus={formikHandleFocus}
                                                      onBlur={formikHandleBlur}
                                                      value={
                                                        itemBefore.value
                                                          ? itemBefore.value
                                                          : itemBefore.mask
                                                          ? itemBefore.mask(formik.values[itemBefore.name])
                                                          : formik.values[itemBefore.name]
                                                      }
                                                      maxLength={itemBefore.maxLength}
                                                      data-error={formik.touched[itemBefore.name] && formik.errors[itemBefore.name]}
                                                      placeholder={itemBefore.placeholder || ''}
                                                      data-removearrows={!!itemBefore.inputNumberRemoveArrows}
                                                      disabled={!!itemBefore.disabled}
                                                    />
                                                  </>
                                                )}

                                                {itemBefore.formType === 'select' && (
                                                  <Select
                                                    id={`item-${itemBefore.name}`}
                                                    name={itemBefore.name}
                                                    data-error={formik.touched[itemBefore.name] && formik.errors[itemBefore.name]}
                                                    onChange={formikHandleChange}
                                                    disabled={!!itemBefore.disabled}
                                                    defaultValue={formik.values[itemBefore.name]}
                                                  >
                                                    {!itemBefore.selectRemoveFirstOption && (
                                                      <option value={itemBefore.selectFirstValue} hidden>
                                                        {itemBefore.selectFirstLabel || ''}
                                                      </option>
                                                    )}
                                                    {itemBefore.selectOptions &&
                                                      itemBefore.selectOptions.map((optionItem, index) => (
                                                        <option
                                                          key={`option-item-${itemBefore.name}-${index.toString()}`}
                                                          // selected={formik.values[itemBefore.name] === optionItem.value}
                                                          value={optionItem.value}
                                                        >
                                                          {optionItem.label}
                                                        </option>
                                                      ))}
                                                  </Select>
                                                )}
                                              </Box>
                                            );
                                          })
                                        ) : (
                                          <></>
                                        )}

                                        <Box
                                          flexGrow={0}
                                          key={`form-item-${item.name}`}
                                          width={item.width ? item.width : 'inherit'}
                                          maxWidth={item.maxWidth ? item.maxWidth : 'inherit'}
                                        >
                                          {item.formType === 'input' && (
                                            <>
                                              <Input
                                                id={`item-${item.name}`}
                                                name={item.name}
                                                type={item.inputType}
                                                onChange={formikHandleChange}
                                                onFocus={formikHandleFocus}
                                                onBlur={formikHandleBlur}
                                                value={
                                                  item.value ? item.value : item.mask ? item.mask(formik.values[item.name]) : formik.values[item.name]
                                                }
                                                maxLength={item.maxLength}
                                                data-error={formik.touched[item.name] && formik.errors[item.name]}
                                                placeholder={item.placeholder || ''}
                                                data-removearrows={!!item.inputNumberRemoveArrows}
                                                disabled={!!item.disabled}
                                              />
                                            </>
                                          )}

                                          {item.formType === 'select' && (
                                            <Select
                                              id={`item-${item.name}`}
                                              name={item.name}
                                              data-error={formik.touched[item.name] && formik.errors[item.name]}
                                              onChange={formikHandleChange}
                                              disabled={!!item.disabled}
                                              defaultValue={formik.values[item.name]}
                                            >
                                              {!item.selectRemoveFirstOption && (
                                                <option value={item.selectFirstValue} hidden>
                                                  {item.selectFirstLabel}
                                                </option>
                                              )}
                                              {item.selectOptions &&
                                                item.selectOptions.map((optionItem, index) => (
                                                  <option
                                                    key={`option-item-${item.name}-${index.toString()}`}
                                                    // selected={formik.values[item.name] === optionItem.value}
                                                    value={optionItem.value}
                                                  >
                                                    {optionItem.label}
                                                  </option>
                                                ))}
                                            </Select>
                                          )}

                                          {item.formType === 'radio' && (
                                            <Flex flexDirection={item.radioDirection || 'row'}>
                                              {item.radioOptions?.map((radioOptionItem, radioOptionItemIndex) => {
                                                return (
                                                  <Flex
                                                    key={`form-radio-${item.name}-${radioOptionItemIndex.toString()}`}
                                                    ml={radioOptionItemIndex && (item.radioDirection || 'row') === 'row' ? 12 : 0}
                                                  >
                                                    <Radio
                                                      id={`radioItem-${item.name}-${radioOptionItem.value}`}
                                                      name={item.name}
                                                      type="radio"
                                                      disabled={!!item.disabled}
                                                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        formik.setFieldValue(item.name, Number(e.target.value));
                                                      }}
                                                      value={radioOptionItem.value}
                                                      checked={formik.values[item.name] === radioOptionItem.value}
                                                    />

                                                    <label htmlFor={`radioItem-${item.name}-${radioOptionItem.value}`} style={{ cursor: 'pointer' }}>
                                                      <Typography type="type1" style={{ whiteSpace: 'nowrap' }} ml={2}>
                                                        {radioOptionItem.label}
                                                      </Typography>
                                                    </label>
                                                  </Flex>
                                                );
                                              })}
                                            </Flex>
                                          )}
                                        </Box>

                                        {item.after && item.after.length ? (
                                          item.after.map((itemAfter, indexBefore) => {
                                            return (
                                              <Box
                                                flexGrow={0}
                                                key={`form-itemAfter-${itemAfter.name}`}
                                                width={itemAfter.width ? itemAfter.width : 'inherit'}
                                                maxWidth={itemAfter.maxWidth ? itemAfter.maxWidth : 'inherit'}
                                                ml={2}
                                              >
                                                {itemAfter.formType === 'input' && (
                                                  <Input
                                                    id={`item-${itemAfter.name}`}
                                                    name={itemAfter.name}
                                                    type={itemAfter.inputType}
                                                    onChange={formikHandleChange}
                                                    onFocus={formikHandleFocus}
                                                    onBlur={formikHandleBlur}
                                                    value={
                                                      itemAfter.value
                                                        ? itemAfter.value
                                                        : itemAfter.mask
                                                        ? itemAfter.mask(formik.values[itemAfter.name])
                                                        : formik.values[itemAfter.name]
                                                    }
                                                    maxLength={itemAfter.maxLength}
                                                    data-error={formik.touched[itemAfter.name] && formik.errors[itemAfter.name]}
                                                    placeholder={itemAfter.placeholder || ''}
                                                    data-removearrows={!!itemAfter.inputNumberRemoveArrows}
                                                    disabled={!!itemAfter.disabled}
                                                  />
                                                )}

                                                {itemAfter.formType === 'select' && (
                                                  <Select
                                                    id={`item-${itemAfter.name}`}
                                                    name={itemAfter.name}
                                                    data-error={formik.touched[itemAfter.name] && formik.errors[itemAfter.name]}
                                                    onChange={formikHandleChange}
                                                    defaultValue={formik.values[itemAfter.name]}
                                                    disabled={!!itemAfter.disabled}
                                                  >
                                                    {!itemAfter.selectRemoveFirstOption && (
                                                      <option value={itemAfter.selectFirstValue} hidden>
                                                        {itemAfter.selectFirstLabel || ''}
                                                      </option>
                                                    )}
                                                    {itemAfter.selectOptions &&
                                                      itemAfter.selectOptions.map((optionItem, index) => (
                                                        <option
                                                          key={`option-item-${itemAfter.name}-${index.toString()}`}
                                                          // selected={formik.values[itemAfter.name] === optionItem.value}
                                                          value={optionItem.value}
                                                        >
                                                          {optionItem.label}
                                                        </option>
                                                      ))}
                                                  </Select>
                                                )}

                                                {itemAfter.formType === 'checkbox' && (
                                                  <Flex flexWrap="nowrap" alignItems="center">
                                                    <Checkbox
                                                      id={`item-${itemAfter.name}`}
                                                      name={itemAfter.name}
                                                      type={itemAfter.inputType}
                                                      onChange={formikHandleChange}
                                                      value="1"
                                                      checked={Boolean(formik.values[itemAfter.name])}
                                                      disabled={!!itemAfter.disabled}
                                                    />

                                                    {Boolean(itemAfter.checkboxLabel) && (
                                                      <label htmlFor={`item-${itemAfter.name}`}>
                                                        <Typography type="type1" style={{ whiteSpace: 'nowrap' }} ml={2}>
                                                          {itemAfter.checkboxLabel}
                                                        </Typography>
                                                      </label>
                                                    )}
                                                  </Flex>
                                                )}
                                              </Box>
                                            );
                                          })
                                        ) : (
                                          <></>
                                        )}
                                      </Flex>
                                    </Flex>

                                    {formik.touched[item.name] && formik.errors[item.name] ? (
                                      <HelperText type="error">{formik.errors[item.name]}</HelperText>
                                    ) : null}
                                  </Flex>
                                </Flex>

                                {Boolean(item.rightText) && (
                                  <Flex flexGrow={0} alignItems="center" height={38} mt={{ m: 1, d: '43px' }} ml={{ d: 3 }}>
                                    <TextIcon icon="alertCircle" />
                                    <Typography type="type0" ml={{ d: 1 }} color="gray_500">
                                      <div dangerouslySetInnerHTML={createMarkup(item.rightText)} />
                                    </Typography>
                                  </Flex>
                                )}
                              </Flex>
                            )
                          )}
                        </Flex>
                      );
                    })
                  : null}
                {subAreaIndex === 1
                  ? generateFormContact().map((rowItem, rowIndex) => {
                      return (
                        <Flex
                          key={`form-first-${rowIndex.toString()}`}
                          flexGrow={{ m: 1, d: 0 }}
                          flexDirection={{ m: 'column', d: 'row' }}
                          flexWrap={{ m: 'wrap', d: 'nowrap' }}
                        >
                          {rowItem.map((item, index) =>
                            item.hide ? (
                              <></>
                            ) : item.icon ? (
                              <Flex
                                key={`form-social-item-${index.toString()}`}
                                flexDirection="column"
                                mt={4}
                                ml={index ? 2 : 0}
                                width={item && item.width ? item.width : 'inherit'}
                                maxWidth={item && item.maxWidth ? item.maxWidth : 'inherit'}
                                flexShrink={0}
                                flexGrow={0}
                              >
                                <Flex flexDirection="column">
                                  <Flex mt={1} flexWrap="nowrap" alignItems="center" position="relative">
                                    <IconWrapper title={item.label}>
                                      {Boolean(item.icon) && <FormIcon icon={item.icon ? item.icon : 'search'} />}
                                    </IconWrapper>

                                    {item.formType === 'input' && (
                                      <InputIcon
                                        id={`item-${item.name}`}
                                        name={item.name}
                                        type={item.inputType}
                                        onChange={formikHandleChange}
                                        onFocus={formikHandleFocus}
                                        onBlur={formikHandleBlur}
                                        value={item.value ? item.value : item.mask ? item.mask(formik.values[item.name]) : formik.values[item.name]}
                                        maxLength={item.maxLength}
                                        data-error={formik.touched[item.name] && formik.errors[item.name]}
                                      />
                                    )}
                                  </Flex>

                                  {formik.touched[item.name] && formik.errors[item.name] ? (
                                    <HelperText type="error">{formik.errors[item.name]}</HelperText>
                                  ) : null}
                                </Flex>
                              </Flex>
                            ) : (
                              <Flex
                                key={`form-first-item-${item.name}`}
                                flexGrow={{ m: 1, d: 0 }}
                                // flexDirection={{ m: 'column', d: 'row' }}
                                // flexWrap={{ m: 'wrap', d: 'nowrap' }}
                                // width={{ m: '100%', d: 'inherit' }}
                              >
                                <Flex
                                  flexDirection="column"
                                  mt={4}
                                  ml={{ d: index ? 3 : 0 }}
                                  width={item && item.width ? item.width : 'inherit'}
                                  maxWidth={item && item.maxWidth ? item.maxWidth : 'inherit'}
                                  flexShrink={0}
                                  flexGrow={1}
                                >
                                  <Flex justifyContent="space-between">
                                    <Flex flexGrow={0}>
                                      <Typography type="type1" fontWeight="bold" upper>
                                        <Label htmlFor={`item-${item.name}`}>{item.label}</Label>
                                      </Typography>
                                    </Flex>
                                  </Flex>

                                  <Flex flexDirection="column">
                                    <Flex mt={1} flexWrap="nowrap" alignItems="center">
                                      <Flex flexGrow={1} flexWrap="nowrap" alignItems="center">
                                        {item.before && item.before.length ? (
                                          item.before.map((itemBefore, indexBefore) => {
                                            return (
                                              <Box
                                                flexGrow={0}
                                                key={`form-itemBefore-${itemBefore.name}`}
                                                width={itemBefore.width ? itemBefore.width : 'inherit'}
                                                maxWidth={itemBefore.maxWidth ? itemBefore.maxWidth : 'inherit'}
                                                mr={2}
                                              >
                                                {itemBefore.formType === 'input' && (
                                                  <>
                                                    <Input
                                                      id={`item-${itemBefore.name}`}
                                                      name={itemBefore.name}
                                                      type={itemBefore.inputType}
                                                      onChange={formikHandleChange}
                                                      onFocus={formikHandleFocus}
                                                      onBlur={formikHandleBlur}
                                                      value={
                                                        itemBefore.value
                                                          ? itemBefore.value
                                                          : itemBefore.mask
                                                          ? itemBefore.mask(formik.values[itemBefore.name])
                                                          : formik.values[itemBefore.name]
                                                      }
                                                      maxLength={itemBefore.maxLength}
                                                      data-error={formik.touched[itemBefore.name] && formik.errors[itemBefore.name]}
                                                      placeholder={itemBefore.placeholder || ''}
                                                      data-removearrows={!!itemBefore.inputNumberRemoveArrows}
                                                      disabled={!!itemBefore.disabled}
                                                    />
                                                  </>
                                                )}

                                                {itemBefore.formType === 'select' && (
                                                  <Select
                                                    id={`item-${itemBefore.name}`}
                                                    name={itemBefore.name}
                                                    data-error={formik.touched[itemBefore.name] && formik.errors[itemBefore.name]}
                                                    onChange={formikHandleChange}
                                                    disabled={!!itemBefore.disabled}
                                                    defaultValue={formik.values[itemBefore.name]}
                                                  >
                                                    {!itemBefore.selectRemoveFirstOption && (
                                                      <option value={itemBefore.selectFirstValue} hidden>
                                                        {itemBefore.selectFirstLabel || ''}
                                                      </option>
                                                    )}
                                                    {itemBefore.selectOptions &&
                                                      itemBefore.selectOptions.map((optionItem, index) => (
                                                        <option
                                                          key={`option-item-${itemBefore.name}-${index.toString()}`}
                                                          // selected={formik.values[itemBefore.name] === optionItem.value}
                                                          value={optionItem.value}
                                                        >
                                                          {optionItem.label}
                                                        </option>
                                                      ))}
                                                  </Select>
                                                )}
                                              </Box>
                                            );
                                          })
                                        ) : (
                                          <></>
                                        )}

                                        <Box
                                          flexGrow={0}
                                          key={`form-item-${item.name}`}
                                          width={item.width ? item.width : 'inherit'}
                                          maxWidth={item.maxWidth ? item.maxWidth : 'inherit'}
                                        >
                                          {item.formType === 'input' && (
                                            <>
                                              <Input
                                                id={`item-${item.name}`}
                                                name={item.name}
                                                type={item.inputType}
                                                onChange={formikHandleChange}
                                                onFocus={formikHandleFocus}
                                                onBlur={formikHandleBlur}
                                                value={
                                                  item.value ? item.value : item.mask ? item.mask(formik.values[item.name]) : formik.values[item.name]
                                                }
                                                maxLength={item.maxLength}
                                                data-error={formik.touched[item.name] && formik.errors[item.name]}
                                                placeholder={item.placeholder || ''}
                                                data-removearrows={!!item.inputNumberRemoveArrows}
                                                disabled={!!item.disabled}
                                              />
                                            </>
                                          )}

                                          {item.formType === 'select' && (
                                            <Select
                                              id={`item-${item.name}`}
                                              name={item.name}
                                              data-error={formik.touched[item.name] && formik.errors[item.name]}
                                              onChange={formikHandleChange}
                                              disabled={!!item.disabled}
                                              defaultValue={formik.values[item.name]}
                                            >
                                              {!item.selectRemoveFirstOption && (
                                                <option value={item.selectFirstValue} hidden>
                                                  {item.selectFirstLabel}
                                                </option>
                                              )}
                                              {item.selectOptions &&
                                                item.selectOptions.map((optionItem, index) => (
                                                  <option
                                                    key={`option-item-${item.name}-${index.toString()}`}
                                                    // selected={formik.values[item.name] === optionItem.value}
                                                    value={optionItem.value}
                                                  >
                                                    {optionItem.label}
                                                  </option>
                                                ))}
                                            </Select>
                                          )}

                                          {item.formType === 'radio' && (
                                            <Flex flexDirection={item.radioDirection || 'row'}>
                                              {item.radioOptions?.map((radioOptionItem, radioOptionItemIndex) => {
                                                return (
                                                  <Flex
                                                    key={`form-radio-${item.name}-${radioOptionItemIndex.toString()}`}
                                                    ml={radioOptionItemIndex && (item.radioDirection || 'row') === 'row' ? 12 : 0}
                                                  >
                                                    <Radio
                                                      id={`radioItem-${item.name}-${radioOptionItem.value}`}
                                                      name={item.name}
                                                      type="radio"
                                                      disabled={!!item.disabled}
                                                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        formik.setFieldValue(item.name, Number(e.target.value));
                                                      }}
                                                      value={radioOptionItem.value}
                                                      checked={formik.values[item.name] === radioOptionItem.value}
                                                    />

                                                    <label htmlFor={`radioItem-${item.name}-${radioOptionItem.value}`} style={{ cursor: 'pointer' }}>
                                                      <Typography type="type1" style={{ whiteSpace: 'nowrap' }} ml={2}>
                                                        {radioOptionItem.label}
                                                      </Typography>
                                                    </label>
                                                  </Flex>
                                                );
                                              })}
                                            </Flex>
                                          )}
                                        </Box>

                                        {item.after && item.after.length ? (
                                          item.after.map((itemAfter, indexBefore) => {
                                            return (
                                              <Box
                                                flexGrow={0}
                                                key={`form-itemAfter-${itemAfter.name}`}
                                                width={itemAfter.width ? itemAfter.width : 'inherit'}
                                                maxWidth={itemAfter.maxWidth ? itemAfter.maxWidth : 'inherit'}
                                                ml={2}
                                              >
                                                {itemAfter.formType === 'input' && (
                                                  <Input
                                                    id={`item-${itemAfter.name}`}
                                                    name={itemAfter.name}
                                                    type={itemAfter.inputType}
                                                    onChange={formikHandleChange}
                                                    onFocus={formikHandleFocus}
                                                    onBlur={formikHandleBlur}
                                                    value={
                                                      itemAfter.value
                                                        ? itemAfter.value
                                                        : itemAfter.mask
                                                        ? itemAfter.mask(formik.values[itemAfter.name])
                                                        : formik.values[itemAfter.name]
                                                    }
                                                    maxLength={itemAfter.maxLength}
                                                    data-error={formik.touched[itemAfter.name] && formik.errors[itemAfter.name]}
                                                    placeholder={itemAfter.placeholder || ''}
                                                    data-removearrows={!!itemAfter.inputNumberRemoveArrows}
                                                    disabled={!!itemAfter.disabled}
                                                  />
                                                )}

                                                {itemAfter.formType === 'select' && (
                                                  <Select
                                                    id={`item-${itemAfter.name}`}
                                                    name={itemAfter.name}
                                                    data-error={formik.touched[itemAfter.name] && formik.errors[itemAfter.name]}
                                                    onChange={formikHandleChange}
                                                    defaultValue={formik.values[itemAfter.name]}
                                                    disabled={!!itemAfter.disabled}
                                                  >
                                                    {!itemAfter.selectRemoveFirstOption && (
                                                      <option value={itemAfter.selectFirstValue} hidden>
                                                        {itemAfter.selectFirstLabel || ''}
                                                      </option>
                                                    )}
                                                    {itemAfter.selectOptions &&
                                                      itemAfter.selectOptions.map((optionItem, index) => (
                                                        <option
                                                          key={`option-item-${itemAfter.name}-${index.toString()}`}
                                                          // selected={formik.values[itemAfter.name] === optionItem.value}
                                                          value={optionItem.value}
                                                        >
                                                          {optionItem.label}
                                                        </option>
                                                      ))}
                                                  </Select>
                                                )}

                                                {itemAfter.formType === 'checkbox' && (
                                                  <Flex flexWrap="nowrap" alignItems="center">
                                                    <Checkbox
                                                      id={`item-${itemAfter.name}`}
                                                      name={itemAfter.name}
                                                      type={itemAfter.inputType}
                                                      onChange={formikHandleChange}
                                                      value="1"
                                                      checked={Boolean(formik.values[itemAfter.name])}
                                                      disabled={!!itemAfter.disabled}
                                                    />

                                                    {Boolean(itemAfter.checkboxLabel) && (
                                                      <label htmlFor={`item-${itemAfter.name}`}>
                                                        <Typography type="type1" style={{ whiteSpace: 'nowrap' }} ml={2}>
                                                          {itemAfter.checkboxLabel}
                                                        </Typography>
                                                      </label>
                                                    )}
                                                  </Flex>
                                                )}
                                              </Box>
                                            );
                                          })
                                        ) : (
                                          <></>
                                        )}
                                      </Flex>
                                    </Flex>

                                    {formik.touched[item.name] && formik.errors[item.name] ? (
                                      <HelperText type="error">{formik.errors[item.name]}</HelperText>
                                    ) : null}
                                  </Flex>
                                </Flex>

                                {Boolean(item.rightText) && (
                                  <Flex flexGrow={0} alignItems="center" height={38} mt={{ m: 1, d: '43px' }}>
                                    <Typography type="type0" ml={{ d: 3 }}>
                                      <div dangerouslySetInnerHTML={createMarkup(item.rightText)} />
                                    </Typography>
                                  </Flex>
                                )}
                              </Flex>
                            )
                          )}
                        </Flex>
                      );
                    })
                  : null}

                {subAreaIndex === 2
                  ? generateFormAddress().map((rowItem, rowIndex) => {
                      return (
                        <Flex
                          key={`form-address-${rowIndex.toString()}`}
                          flexGrow={{ m: 1, d: 0 }}
                          flexDirection={{ m: 'column', d: 'row' }}
                          flexWrap={{ m: 'wrap', d: 'nowrap' }}
                        >
                          {rowItem.map((item, index) =>
                            item.hide ? (
                              <></>
                            ) : (
                              <Flex
                                key={`form-address-item-${item.name}-${index.toString()}`}
                                flexGrow={{ m: 1, d: 0 }}
                                alignItems="flex-start"
                                // flexDirection={{ m: 'column', d: 'row' }}
                                // flexWrap={{ m: 'wrap', d: 'nowrap' }}
                                // width={{ m: '100%', d: 'inherit' }}
                              >
                                <Flex
                                  flexDirection="column"
                                  mt={4}
                                  ml={{ d: index ? 3 : 0 }}
                                  width={item && item.width ? item.width : 'inherit'}
                                  maxWidth={item && item.maxWidth ? item.maxWidth : 'inherit'}
                                  flexShrink={0}
                                  flexGrow={1}
                                >
                                  <Flex justifyContent="space-between">
                                    <Flex flexGrow={0}>
                                      <Typography type="type1" fontWeight="bold" upper>
                                        <Label htmlFor={`item-${item.name}`}>{item.label}</Label>
                                      </Typography>
                                    </Flex>
                                  </Flex>

                                  <Flex flexDirection="column">
                                    <Flex mt={1} flexWrap="nowrap" alignItems="center">
                                      <Flex flexGrow={1} flexWrap="nowrap" alignItems="center">
                                        {item.before && item.before.length ? (
                                          item.before.map((itemBefore, indexBefore) => {
                                            return (
                                              <Box
                                                flexGrow={0}
                                                key={`form-itemBefore-${itemBefore.name}`}
                                                width={itemBefore.width ? itemBefore.width : 'inherit'}
                                                maxWidth={itemBefore.maxWidth ? itemBefore.maxWidth : 'inherit'}
                                                mr={2}
                                              >
                                                {itemBefore.formType === 'input' && (
                                                  <Input
                                                    id={`item-${itemBefore.name}`}
                                                    name={itemBefore.name}
                                                    type={itemBefore.inputType}
                                                    onChange={formikHandleChange}
                                                    onFocus={formikHandleFocus}
                                                    onBlur={formikHandleBlur}
                                                    value={
                                                      itemBefore.value
                                                        ? itemBefore.value
                                                        : itemBefore.mask
                                                        ? itemBefore.mask(formik.values[itemBefore.name])
                                                        : formik.values[itemBefore.name]
                                                    }
                                                    maxLength={itemBefore.maxLength}
                                                    data-error={formik.touched[itemBefore.name] && formik.errors[itemBefore.name]}
                                                    placeholder={itemBefore.placeholder || ''}
                                                    data-removearrows={!!itemBefore.inputNumberRemoveArrows}
                                                  />
                                                )}

                                                {itemBefore.formType === 'select' && (
                                                  <Select
                                                    id={`item-${itemBefore.name}`}
                                                    name={itemBefore.name}
                                                    data-error={formik.touched[itemBefore.name] && formik.errors[itemBefore.name]}
                                                    onChange={formikHandleChange}
                                                    disabled={itemBefore.disabled}
                                                    defaultValue={formik.values[itemBefore.name]}
                                                  >
                                                    {!itemBefore.selectRemoveFirstOption && (
                                                      <option value={itemBefore.selectFirstValue} hidden>
                                                        {itemBefore.selectFirstLabel || ''}
                                                      </option>
                                                    )}
                                                    {itemBefore.selectOptions &&
                                                      itemBefore.selectOptions.map((optionItem, index) => (
                                                        <option
                                                          key={`option-item-${itemBefore.name}-${index.toString()}`}
                                                          // selected={formik.values[itemBefore.name] === optionItem.value}
                                                          value={optionItem.value}
                                                        >
                                                          {optionItem.label}
                                                        </option>
                                                      ))}
                                                  </Select>
                                                )}
                                              </Box>
                                            );
                                          })
                                        ) : (
                                          <></>
                                        )}

                                        {item.formType === 'input' && (
                                          <Input
                                            id={`item-${item.name}`}
                                            name={item.name}
                                            type={item.inputType}
                                            onChange={item.inputOnChange || formikHandleChange}
                                            onFocus={formikHandleFocus}
                                            onBlur={formikHandleBlur}
                                            value={
                                              item.value ? item.value : item.mask ? item.mask(formik.values[item.name]) : formik.values[item.name]
                                            }
                                            maxLength={item.maxLength}
                                            data-error={formik.touched[item.name] && formik.errors[item.name]}
                                            placeholder={item.placeholder || ''}
                                            data-removearrows={!!item.inputNumberRemoveArrows}
                                          />
                                        )}

                                        {item.formType === 'select' && (
                                          <Select
                                            id={`item-${item.name}`}
                                            name={item.name}
                                            data-error={formik.touched[item.name] && formik.errors[item.name]}
                                            onChange={formikHandleChange}
                                            disabled={item.disabled}
                                            defaultValue={formik.values[item.name]}
                                          >
                                            {!item.selectRemoveFirstOption && (
                                              <option value={item.selectFirstValue} hidden>
                                                {item.selectFirstLabel}
                                              </option>
                                            )}
                                            {item.selectOptions &&
                                              item.selectOptions.map((optionItem, index) => (
                                                <option
                                                  key={`option-item-${item.name}-${index.toString()}`}
                                                  // selected={formik.values[item.name] === optionItem.value}
                                                  value={optionItem.value}
                                                >
                                                  {optionItem.label}
                                                </option>
                                              ))}
                                          </Select>
                                        )}

                                        {item.after && item.after.length ? (
                                          item.after.map((itemAfter, indexBefore) => {
                                            return (
                                              <Box
                                                flexGrow={0}
                                                key={`form-itemAfter-${itemAfter.name}`}
                                                width={itemAfter.width ? itemAfter.width : 'inherit'}
                                                maxWidth={itemAfter.maxWidth ? itemAfter.maxWidth : 'inherit'}
                                                ml={2}
                                              >
                                                {itemAfter.formType === 'input' && (
                                                  <Input
                                                    id={`item-${itemAfter.name}`}
                                                    name={itemAfter.name}
                                                    type={itemAfter.inputType}
                                                    onChange={formikHandleChange}
                                                    onFocus={formikHandleFocus}
                                                    onBlur={formikHandleBlur}
                                                    value={
                                                      itemAfter.value
                                                        ? itemAfter.value
                                                        : itemAfter.mask
                                                        ? itemAfter.mask(formik.values[itemAfter.name])
                                                        : formik.values[itemAfter.name]
                                                    }
                                                    maxLength={itemAfter.maxLength}
                                                    data-error={formik.touched[itemAfter.name] && formik.errors[itemAfter.name]}
                                                    placeholder={itemAfter.placeholder || ''}
                                                    data-removearrows={!!itemAfter.inputNumberRemoveArrows}
                                                  />
                                                )}

                                                {itemAfter.formType === 'select' && (
                                                  <Select
                                                    id={`item-${itemAfter.name}`}
                                                    name={itemAfter.name}
                                                    data-error={formik.touched[itemAfter.name] && formik.errors[itemAfter.name]}
                                                    onChange={formikHandleChange}
                                                    disabled={itemAfter.disabled}
                                                    defaultValue={formik.values[itemAfter.name]}
                                                  >
                                                    {!itemAfter.selectRemoveFirstOption && (
                                                      <option value={itemAfter.selectFirstValue} hidden>
                                                        {itemAfter.selectFirstLabel || ''}
                                                      </option>
                                                    )}
                                                    {itemAfter.selectOptions &&
                                                      itemAfter.selectOptions.map((optionItem, index) => (
                                                        <option
                                                          key={`option-item-${itemAfter.name}-${index.toString()}`}
                                                          // selected={formik.values[itemAfter.name] === optionItem.value}
                                                          value={optionItem.value}
                                                        >
                                                          {optionItem.label}
                                                        </option>
                                                      ))}
                                                  </Select>
                                                )}
                                              </Box>
                                            );
                                          })
                                        ) : (
                                          <></>
                                        )}
                                      </Flex>
                                    </Flex>

                                    {formik.touched[item.name] && formik.errors[item.name] ? (
                                      <HelperText type="error">{formik.errors[item.name]}</HelperText>
                                    ) : null}
                                  </Flex>
                                </Flex>

                                {Boolean(item.rightText) && (
                                  <Flex flexGrow={0} alignItems="center" height={38} mt={{ m: 1, d: '43px' }}>
                                    <Typography type="type0" ml={{ d: 3 }}>
                                      <div dangerouslySetInnerHTML={createMarkup(item.rightText)} />
                                    </Typography>
                                  </Flex>
                                )}
                              </Flex>
                            )
                          )}
                        </Flex>
                      );
                    })
                  : null}

                {subAreaIndex === 3 ? (
                  <>
                    <ChangeEmail opened onClose={() => {}} />
                  </>
                ) : null}

                <Flex mt={3}>
                  {Boolean(Object.values(formik.errors).length) && (
                    <Flex bg="alert_400" p={3} borderRadius={4}>
                      <Typography type="type2" fontWeight="bold" color="white">
                        Preencha corretamente os campos acima em destaque.
                      </Typography>
                    </Flex>
                  )}

                  <Alert />
                </Flex>

                {subAreaIndex !== 3 ? (
                  <>
                    <Flex mt={3}>
                      <Button disabled={Boolean(Object.values(formik.errors).length)} type="submit">
                        Salvar Alterações
                      </Button>
                    </Flex>
                  </>
                ) : null}
              </Flex>
            </Section>
          </Flex>
        </form>
      </Flex>

      {confirmCurrentEmail && <ConfirmCurrentEmail />}
      {confirmNewEmail && <ConfirmNewEmail />}

      <ChangeEmail opened={changeEmailOpen} onClose={() => setChangeEmailOpen(false)} />
    </Page>
  );
};

export default AccountView;
